import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/auth-context";

const Login: React.FC = () => {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const { login } = useAuth();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError("");

        try {
            const success = await login(username, password);
            if (success) {
                navigate("/dashboard");
            } else {
                setError("Invalid username or password");
            }
        } catch (err) {
            setError("Something went wrong");
            console.error("Login error:", err);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div 
            className="flex min-h-screen items-center justify-center p-6"
            style={{
                background: '#f8fafc',
            }}
        >
            <div 
                className="w-full max-w-md bg-white rounded-xl p-8 space-y-6"
                style={{
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.03)',
                    border: '1px solid #e2e8f0',
                }}
            >
                {/* Header */}
                <div className="text-center">
                    <div className="flex justify-center mb-5">
                        <div 
                            className="w-14 h-14 rounded-xl flex items-center justify-center"
                            style={{
                                background: '#3b82f6',
                            }}
                        >
                            <svg viewBox="0 0 24 24" className="w-8 h-8" fill="white">
                                <rect x="4" y="4" width="6" height="6" rx="1.5" opacity="0.9" />
                                <rect x="14" y="4" width="6" height="6" rx="1.5" opacity="0.7" />
                                <rect x="4" y="14" width="6" height="6" rx="1.5" opacity="0.7" />
                                <rect x="14" y="14" width="6" height="6" rx="1.5" opacity="0.5" />
                            </svg>
                        </div>
                    </div>
                    <h2 
                        className="text-xl font-semibold"
                        style={{ 
                            color: '#0f172a',
                            letterSpacing: '-0.01em',
                        }}
                    >
                        Sign in to FrontDesk
                    </h2>
                    <p 
                        className="mt-1 text-sm"
                        style={{ color: '#64748b' }}
                    >
                        Enter your credentials to continue
                    </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                    {error && (
                        <div 
                            className="p-3 rounded-lg text-center text-sm font-medium"
                            style={{
                                background: '#fee2e2',
                                color: '#dc2626',
                            }}
                        >
                            {error}
                        </div>
                    )}

                    {/* Username Field */}
                    <div className="space-y-1.5">
                        <label 
                            htmlFor="username" 
                            className="block text-sm font-medium"
                            style={{ color: '#475569' }}
                        >
                            Username
                        </label>
                        <input
                            id="username"
                            type="text"
                            placeholder="Enter your username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            className="block w-full px-3 py-2.5 rounded-lg text-sm transition-all duration-150 outline-none"
                            style={{
                                border: '1px solid #e2e8f0',
                                background: '#ffffff',
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = '#3b82f6';
                                e.target.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.08)';
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = '#e2e8f0';
                                e.target.style.boxShadow = 'none';
                            }}
                            required
                        />
                    </div>

                    {/* Password Field */}
                    <div className="space-y-1.5">
                        <label 
                            htmlFor="password" 
                            className="block text-sm font-medium"
                            style={{ color: '#475569' }}
                        >
                            Password
                        </label>
                        <input
                            id="password"
                            type="password"
                            placeholder="Enter your password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="block w-full px-3 py-2.5 rounded-lg text-sm transition-all duration-150 outline-none"
                            style={{
                                border: '1px solid #e2e8f0',
                                background: '#ffffff',
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = '#3b82f6';
                                e.target.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.08)';
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = '#e2e8f0';
                                e.target.style.boxShadow = 'none';
                            }}
                            required
                        />
                    </div>

                    {/* Forgot Password Link */}
                    <div className="text-right">
                        <a 
                            href="#" 
                            className="text-sm font-medium transition-colors"
                            style={{ color: '#3b82f6' }}
                            onMouseOver={(e) => e.currentTarget.style.color = '#2563eb'}
                            onMouseOut={(e) => e.currentTarget.style.color = '#3b82f6'}
                        >
                            Forgot password?
                        </a>
                    </div>

                    {/* Sign In Button */}
                    <button
                        type="submit"
                        className="w-full py-2.5 rounded-lg font-medium text-white text-sm transition-all duration-150 flex items-center justify-center"
                        style={{
                            background: '#3b82f6',
                        }}
                        onMouseOver={(e) => {
                            if (!isLoading) {
                                e.currentTarget.style.background = '#2563eb';
                            }
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.background = '#3b82f6';
                        }}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                <svg 
                                    className="animate-spin h-4 w-4 mr-2" 
                                    viewBox="0 0 24 24" 
                                    fill="none"
                                >
                                    <circle 
                                        cx="12" 
                                        cy="12" 
                                        r="10" 
                                        stroke="currentColor" 
                                        strokeWidth="3"
                                        strokeOpacity="0.25"
                                    />
                                    <path 
                                        d="M12 2a10 10 0 0 1 10 10" 
                                        stroke="currentColor" 
                                        strokeWidth="3"
                                        strokeLinecap="round"
                                    />
                                </svg>
                                Signing in...
                            </>
                        ) : (
                            "Sign in"
                        )}
                    </button>
                </form>

                {/* Footer */}
                <div 
                    className="text-center text-sm pt-4"
                    style={{ 
                        borderTop: '1px solid #f1f5f9',
                        color: '#94a3b8',
                    }}
                >
                    <p>
                        Need help?{' '}
                        <a 
                            href="#" 
                            className="font-medium"
                            style={{ color: '#3b82f6' }}
                        >
                            Contact support
                        </a>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Login;
